import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Alert, State } from '../../../../models/api/alerts/alert';
import { ListItem } from '../../../../models/api/core/list/list-item';
import { AlertsService } from '../../../../services/alerts/alerts.service';
import { BaseComponent } from '../../../base/base.component';
import { ButtonComponent } from '../../../core/buttons/button.component';
import { AccountSelectorComponent } from '../../../core/controls/account-selector/account-selector.component';
import { CorpStructSelectorComponent } from '../../../core/controls/corp-struct-selector/corp-struct-selector.component';
import { PickListGroup } from '../../../core/controls/pick-list.component';
import { SelectComponent } from '../../../core/controls/select.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { RequiredFieldsNoteComponent } from '../../../core/labels/required-fields-note.component';
import { StepperComponent } from '../../../core/stepper/stepper.component';
import { AmountOptionComponent } from '../options/amount-option.component';
import { BenefitStatusOptionComponent } from '../options/benefit-status-option.component';
import { BoolOptionComponent } from '../options/bool-option.component';
import { CauseCodeOptionComponent } from '../options/cause-code-option.component';
import { ClaimStatusOptionComponent } from '../options/claim-status-option.component';
import { ClaimTypeOptionComponent } from '../options/claim-type-option.component';
import { ClaimantOptionComponent } from '../options/claimant-option.component';
import { CustomFieldOptionComponent } from '../options/custom-field-option.component';
import { DaysNotifyOptionComponent } from '../options/days-notify-option.component';
import { DaysOptionComponent } from '../options/days-option.component';
import { LagTimeOptionComponent } from '../options/lag-time-option.component';
import { LostTimeOptionComponent } from '../options/lost-time-option.component';
import { PayCodeOptionComponent } from '../options/pay-code-option.component';
import { PaymentOptionComponent } from '../options/payment-option.component';
import { PolicyPeriodOptionComponent } from '../options/policy-period-option.component';
import { SummaryOptionComponent } from '../options/summary-option.component';
import { WorkStatusOptionComponent } from '../options/work-status-option.component';

@Component({
  selector: 'app-alert-options-step',
  imports: [
    ButtonComponent,
    ReactiveFormsModule,
    SelectComponent,
    PageHeaderComponent,
    CommonModule,
    AccountSelectorComponent,
    CorpStructSelectorComponent,
    ClaimStatusOptionComponent,
    ClaimTypeOptionComponent,
    WorkStatusOptionComponent,
    PayCodeOptionComponent,
    BenefitStatusOptionComponent,
    CauseCodeOptionComponent,
    BoolOptionComponent,
    AmountOptionComponent,
    PaymentOptionComponent,
    DaysOptionComponent,
    DaysNotifyOptionComponent,
    SummaryOptionComponent,
    LostTimeOptionComponent,
    ClaimantOptionComponent,
    PolicyPeriodOptionComponent,
    LagTimeOptionComponent,
    CustomFieldOptionComponent,
    RequiredFieldsNoteComponent,
  ],
  template: `
    <h2 class="tw-sr-only">Options</h2>
    <div class="tw-grid tw-grid-cols-2 tw-gap-4 tw-py-8">
      <app-page-header
        class="tw-col-span-2"
        label="Line of business"
        headerSize="h2"
      >
        <ng-template #right>
          <app-required-fields-note classes="tw-pb-0 tw-mb-0" />
        </ng-template>
      </app-page-header>

      <div role="group" aria-labelledby="options-lob-desc" class="tw-contents">
        <span
          class="label tw-col-span-2 sm:tw-col-span-1 tw-mb-1 sm:tw-mb-0"
          id="options-lob-desc"
        >
          Select a line of business
        </span>

        <div class="tw-col-span-2 sm:tw-col-span-1 tw-w-full tw-min-w-0">
          <div class="tw-grid tw-grid-cols-1 tw-w-full tw-min-w-0">
            <app-select
              [options]="lob"
              optionLabel="description"
              optionValue="value"
              [control]="fg.controls.lob"
              label="Line of business"
              [multiple]="true"
              [chip]="true"
              [maxSelectedLabels]="10"
            />
          </div>
        </div>
      </div>

      <app-page-header
        class="tw-col-span-2 tw-pt-4"
        [label]="alert?.conditionCard?.title"
        labelCase="none"
        headerSize="h2"
      />

      <div class="tw-col-span-2">
        @switch (alert?.conditionCard?.type) {
          <!-- select options -->
          @case ('ClaimStatus') {
            <app-claim-status-option
              [listControl]="fg.controls.selectOp"
              [control]="fg.controls.selectItems"
              [lobControl]="fg.controls.lob"
            />
          }
          @case ('ClaimType') {
            <app-claim-type-option
              [listControl]="fg.controls.selectOp"
              [control]="fg.controls.selectItems"
            />
          }
          @case ('WorkStatus') {
            <app-work-status-option
              [listControl]="fg.controls.selectOp"
              [control]="fg.controls.selectItems"
            />
          }
          @case ('PaymentPayCode') {
            <app-pay-code-option
              [control]="fg.controls.selectPickListItems"
              [availableControl]="fg.controls.availablePickListItems"
              [lobControl]="fg.controls.lob"
            />
          }
          @case ('BenefitStatus') {
            <app-benefit-status-option
              [listControl]="fg.controls.selectOp"
              [control]="fg.controls.selectItems"
              [lobControl]="fg.controls.lob"
            />
          }
          @case ('CauseCode') {
            <app-cause-code-option
              [control]="fg.controls.selectItems"
              [lobControl]="fg.controls.lob"
              codeType="Cause"
              label="Cause is in"
            />
          }
          @case ('NatureResult') {
            <app-cause-code-option
              [control]="fg.controls.selectItems"
              [lobControl]="fg.controls.lob"
              codeType="Nature"
              label="The nature/result is in"
            />
          }
          @case ('BodyPart') {
            <app-cause-code-option
              [control]="fg.controls.selectItems"
              [lobControl]="fg.controls.lob"
              codeType="BodyPart"
              label="Body part/target is"
            />
          }
          @case ('Litigation') {
            <app-bool-option
              [control]="fg.controls.selectBoolean"
              label="The claim litigation is"
            />
          }
          @case ('Subrogation') {
            <app-bool-option
              [control]="fg.controls.selectBoolean"
              label="The claim subrogation is"
            />
          }
          @case ('TotalIncurred') {
            <app-amount-option
              [control]="fg.controls.amount"
              [listControl]="fg.controls.amountOp"
              label="The total incurred amount"
            />
          }
          @case ('ReserveChange') {
            <app-amount-option
              [control]="fg.controls.amount"
              [listControl]="fg.controls.amountOp"
              label="The total future reserve amount"
            />
          }
          @case ('PaymentAmount') {
            <app-payment-option
              [control]="fg.controls.amount"
              [listControl]="fg.controls.amountOp"
              label="Payment amount is"
            />
          }
          @case ('ClaimDateOpen') {
            <app-days-option
              [control]="fg.controls.days"
              label="The claim date open is within"
            />
          }
          @case ('ExcessNotificationDate') {
            <app-days-option
              [control]="fg.controls.days"
              label="The excess notification date is within"
            />
          }
          @case ('CalendarRestrictedDays') {
            <app-days-notify-option
              [control]="fg.controls.days"
              [notifyControl]="fg.controls.notifyDays"
              label="Number of days since the last restriction meets or exceeds"
            />
          }
          @case ('AggregateCalendarLostDays') {
            <app-days-notify-option
              [control]="fg.controls.days"
              [notifyControl]="fg.controls.notifyDays"
              label="The total calendar lost time days meets or exceeds"
            />
          }
          @case ('AggregateCalendarRestrictedDays') {
            <app-days-notify-option
              [control]="fg.controls.days"
              [notifyControl]="fg.controls.notifyDays"
              label="Total restricted duty and restricted NTL days meets or exceeds"
            />
          }
          @case ('CalendarLostDays') {
            <app-days-notify-option
              [control]="fg.controls.days"
              [notifyControl]="fg.controls.notifyDays"
              label="Number of days off work in current period meets or exceeds"
            />
          }
          @case ('TCMOpenReferralDays') {
            <app-days-notify-option
              [control]="fg.controls.days"
              [notifyControl]="fg.controls.notifyDays"
              label="TCM referral open days meet or exceed"
            />
          }
          @case ('AdjusterChange') {
            <app-summary-option />
          }
          @case ('ClaimReopened') {
            <app-summary-option />
          }
          @case ('ClaimantDeceased') {
            <app-summary-option />
          }
          @case ('MultipleOpenClaims') {
            <app-summary-option />
          }
          @case ('DefenseAttorneyChanged') {
            <app-summary-option />
          }
          @case ('PlaintiffAttorney') {
            <app-summary-option />
          }
          @case ('EventMultipleClaimants') {
            <app-summary-option />
          }
          @case ('PermanentTotalDisability') {
            <app-summary-option />
          }
          @case ('LegalCaseResolved') {
            <app-summary-option />
          }
          @case ('LagTimeAlerts') {
            <app-lag-time-option
              [fromControl]="fg.controls.lagFrom"
              [toControl]="fg.controls.lagTo"
              [opControl]="fg.controls.lagOp"
              [daysControl]="fg.controls.lagDays"
            />
          }
          @case ('ClientDefinedField') {
            <app-custom-field-option [control]="fg.controls.customField" />
          }
          @case ('LostTimeGuidelines') {
            <app-lost-time-option
              [control]="fg.controls.days"
              [notifyControl]="fg.controls.notifyDays"
            />
          }
          @case ('IndividualHasClaimCase') {
            <app-claimant-option
              [control]="fg.controls.individuals"
              [contractNumber]="contractNumber"
            />
          }
          @case ('PolicyDeductibleSirRange') {
            <app-policy-period-option
              [control]="fg.controls.percent"
              [financialControl]="fg.controls.financial"
              [notifyControl]="fg.controls.notifyDays"
              label="The claim/event amount has reached percentage of the deductible/SIR amount"
            />
          }
          @case ('PolicyLimit') {
            <app-policy-period-option
              [control]="fg.controls.percent"
              [financialControl]="fg.controls.financial"
              [notifyControl]="fg.controls.notifyDays"
              policyCodeType="Event"
              label="The event has reached percentage of the policy limit"
            />
          }
        }
      </div>
      <app-page-header
        class="tw-col-span-2 tw-pt-4"
        label="General"
        headerSize="h2"
      />

      <div
        role="group"
        aria-labelledby="options-states-desc"
        class="tw-contents"
      >
        <span
          class="label tw-col-span-2 sm:tw-col-span-1 tw-mb-1 sm:tw-mb-0"
          id="options-states-desc"
        >
          Select state(s)
        </span>

        <div class="tw-col-span-2 sm:tw-col-span-1 tw-w-full tw-min-w-0">
          <div class="tw-grid tw-grid-cols-1 tw-w-full tw-min-w-0">
            <app-select
              [options]="states"
              optionLabel="description"
              optionValue="value"
              class="tw-basis-1/4 tw-pb-2"
              [control]="fg.controls.jurisdictionState"
              label="Jurisdiction state"
              [multiple]="true"
              [multipleFilter]="true"
              [chip]="true"
            />

            <app-select
              [options]="states"
              optionLabel="description"
              optionValue="value"
              [control]="fg.controls.lossState"
              label="Loss state"
              class="tw-basis-1/4 tw-pb-2"
              [multiple]="true"
              [multipleFilter]="true"
              [chip]="true"
            />

            <app-select
              [options]="states"
              optionLabel="description"
              optionValue="value"
              [control]="fg.controls.payrollState"
              label="Payroll state"
              class="tw-basis-1/4 tw-pb-2"
              [multiple]="true"
              [multipleFilter]="true"
              [chip]="true"
            />

            <app-select
              [options]="states"
              optionLabel="description"
              optionValue="value"
              [control]="fg.controls.premiumState"
              label="Premium state"
              class="tw-basis-1/4"
              [multiple]="true"
              [multipleFilter]="true"
              [chip]="true"
            />
          </div>
        </div>
      </div>

      @if (!suppressClientLocations) {
        <div
          role="group"
          aria-labelledby="options-locations-desc"
          class="tw-contents"
        >
          <span
            class="label tw-col-span-2 sm:tw-col-span-1 tw-mb-1 sm:tw-mb-0"
            id="options-locations-desc"
          >
            Select client location(s)
          </span>

          <div class="tw-col-span-2 sm:tw-col-span-1 tw-w-full tw-min-w-0">
            <div class="tw-grid tw-grid-cols-1 tw-w-full tw-min-w-0">
              <app-select
                [options]="locationMethodOptions"
                optionLabel="description"
                optionValue="value"
                [control]="fg.controls.locationMethod"
                label="Select by"
                [filter]="false"
                [virtualScroll]="false"
                [multiple]="false"
              />
            </div>
          </div>

          @if (fg.controls.locationMethod.value === 'CAU') {
            <div class="tw-col-span-2 app-fade-in">
              <app-account-selector
                [control]="fg.controls.accounts"
                [contractNumber]="contractNumber"
              />
            </div>
          } @else if (fg.controls.locationMethod.value === 'Structure') {
            <div class="tw-col-span-2 app-fade-in">
              <app-corp-struct-selector
                [control]="fg.controls.structures"
                [contractNumber]="contractNumber"
              />
            </div>
          }
        </div>
      }
    </div>

    <div
      class="tw-flex tw-gap-2 tw-w-full tw-justify-center sm:tw-justify-end tw-px-1.5 tw-pt-1 tw-pb-1.5"
    >
      <app-button
        class="tw-w-[44%] sm:tw-w-auto"
        emphasis="Low"
        label="Previous"
        classes="tw-w-full sm:tw-w-auto"
        (click)="previous()"
      />
      <app-button
        class="tw-w-[44%] sm:tw-w-auto"
        label="Next"
        classes="tw-w-full sm:tw-w-auto"
        [disabled]="!fg.valid"
        (click)="next()"
      />
    </div>
  `,
  styles: `
    .label {
      font-weight: bold;
      color: var(--sed-engaged-black-color);
      padding-top: 8px;
      text-wrap: balance;
    }
  `,
})
export class AlertOptionsStepComponent extends BaseComponent implements OnInit {
  alerts = inject(AlertsService);

  @Input()
  stepper: StepperComponent;

  alert: Alert;

  fg = new FormGroup({
    lob: new FormControl<string[]>([], Validators.required),
    jurisdictionState: new FormControl<ListItem[]>(null),
    lossState: new FormControl<ListItem[]>(null),
    payrollState: new FormControl<ListItem[]>(null),
    premiumState: new FormControl<ListItem[]>(null),
    locationMethod: new FormControl<string>(null),
    accounts: new FormControl<any[]>([]),
    structures: new FormControl<any[]>([]),
    individuals: new FormControl<any[]>([]),
    days: new FormControl<number>(null),
    notifyDays: new FormControl<number>(null),
    bool: new FormControl<boolean>(null),
    amountOp: new FormControl<ListItem>(null),
    amount: new FormControl<number>(null),
    selectOp: new FormControl<ListItem>(null),
    selectItems: new FormControl<ListItem[]>(null),
    selectPickListItems: new FormControl<PickListGroup[]>([]),
    availablePickListItems: new FormControl<PickListGroup[]>([]),
    selectBoolean: new FormControl<boolean>(null),
    financial: new FormControl<ListItem>(null),
    percent: new FormControl<number>(null),
    lagFrom: new FormControl<ListItem>(null),
    lagTo: new FormControl<ListItem>(null),
    openFrom: new FormControl<number>(null),
    openTo: new FormControl<number>(null),
    customField: new FormControl<any>(null),

    lagOp: new FormControl<ListItem>(null),

    lagDays: new FormControl<number>(null),
  });

  lob: ListItem[];

  states: ListItem[];

  /** Contract number for account/unit search */
  contractNumber = 0;

  private readonly corpStructRestrictedLobs = new Set(['WC', 'AU', 'GL', 'PR']);

  private corpStructRestricted: boolean | null = null;

  get suppressClientLocations(): boolean {
    return this.user?.isAgentUser ?? false;
  }

  get suppressCorporateStructure(): boolean {
    if (this.corpStructRestricted !== true) {
      return false;
    }

    const lobs = this.fg.controls.lob.value || [];
    return lobs.some((lob) =>
      this.corpStructRestrictedLobs.has((lob || '').toUpperCase()),
    );
  }

  get locationMethodOptions(): ListItem[] {
    const options = this.alerts.locationMethod || [];
    if (this.suppressCorporateStructure) {
      return options.filter((m) => m.value !== 'Structure');
    }

    return options;
  }

  async ngOnInit() {
    this.alert = this.alerts.currentAlert();

    if (this.alert?.contracts?.length > 0) {
      this.contractNumber = this.alert.contracts[0].contNum || 0;
    }

    this.patchFormFromAlert();

    if (this.alert?.conditionCard?.type === 'IndividualHasClaimCase') {
      this.fg.controls.individuals.addValidators((control) => {
        const val = control.value;
        return Array.isArray(val) && val.length > 0 ? null : { required: true };
      });
      this.fg.controls.individuals.updateValueAndValidity();
    }

    if (this.alert) {
      this.alert.linesOfBusiness = this.fg.controls.lob.value || [];
    }

    this.dispose.subs = this.fg.controls.lob.valueChanges.subscribe((value) => {
      if (this.alert) {
        this.alert.linesOfBusiness = value || [];
      }

      this.clearStructureIfSuppressed();
    });

    this.dispose.subs = this.fg.controls.locationMethod.valueChanges.subscribe(
      (value) => {
        if (!value) {
          this.fg.controls.accounts.setValue(null);
          this.fg.controls.structures.setValue(null);
          (this.alert as any).accounts = null;
          (this.alert as any).structures = null;
          this.alert.locations = [];
        }
      },
    );

    if (this.contractNumber > 0 && !this.suppressClientLocations) {
      try {
        const visibility =
          await this.api.filtersCriteria.getFilterGroupVisibility(
            this.contractNumber.toString(),
          );
        this.corpStructRestricted = visibility?.isCorpStructRestricted ?? true;
      } catch {
        this.corpStructRestricted = true;
      }
    } else {
      this.corpStructRestricted = true;
    }

    this.clearStructureIfSuppressed();

    const ruleConditionId = this.alert?.conditionCard?.ruleId ?? 0;
    if (ruleConditionId) {
      await this.alerts.loadConditionExpressions(ruleConditionId);
    }

    const listResponse = await this.api.list.get();

    const supportedLobs = this.alert?.conditionCard?.lobs || [];
    this.lob = listResponse.linesOfBusiness?.filter(
      (item) =>
        item.value !== 'AD' &&
        (supportedLobs.length === 0 || supportedLobs.includes(item.value)),
    );

    this.states = listResponse.states;

    // Keep selected LOBs aligned with the filtered options list
    if (supportedLobs.length > 0) {
      const current = this.fg.controls.lob.value || [];
      const intersected = current.filter((lob) => supportedLobs.includes(lob));
      if (intersected.length !== current.length) {
        this.fg.controls.lob.setValue(intersected);
        this.alert.linesOfBusiness = intersected;
        this.alert.linesOfBusinessCard = intersected;
      }
    }
  }

  override ngOnDestroy(): void {
    if (this.alert && this.alerts.currentAlert()) {
      this.saveFormToAlert();
    }

    super.ngOnDestroy();
  }

  private clearStructureIfSuppressed(): void {
    if (
      this.suppressCorporateStructure &&
      this.fg.controls.locationMethod.value === 'Structure'
    ) {
      this.fg.controls.locationMethod.setValue(null);
    }
  }

  private patchFormFromAlert() {
    if (!this.alert) {
      return;
    }

    if (this.alert.linesOfBusiness?.length > 0) {
      this.fg.controls.lob.setValue(this.alert.linesOfBusiness);
    } else if (this.alert.linesOfBusinessCard?.length > 0) {
      this.fg.controls.lob.setValue(this.alert.linesOfBusinessCard);
    }

    this.setStateFromModel(
      this.fg.controls.jurisdictionState,
      this.alert.statesOfJurisdiction,
    );
    this.setStateFromModel(this.fg.controls.lossState, this.alert.statesOfLoss);
    this.setStateFromModel(
      this.fg.controls.payrollState,
      this.alert.statesOfPayroll,
    );
    this.setStateFromModel(
      this.fg.controls.premiumState,
      this.alert.statesOfPremium,
    );

    const wizardLocationMethod = (this.alert as any).locationMethod;
    const apiLocationMethod = this.alert.locationLookupType;
    const locationMethod =
      wizardLocationMethod ||
      (apiLocationMethod !== 'Unknown' && this.alert.locations?.length > 0
        ? apiLocationMethod
        : null);
    if (locationMethod) {
      this.fg.controls.locationMethod.setValue(locationMethod);
    }

    if ((this.alert as any).accounts) {
      this.fg.controls.accounts.setValue((this.alert as any).accounts);
    } else if (locationMethod === 'CAU' && this.alert.locations?.length > 0) {
      const accounts = this.alert.locations.map((loc) => ({
        id: loc.unitNumber
          ? `${loc.accountNumber} / ${loc.unitNumber}`
          : `${loc.accountNumber}`,
        accountNumber: loc.accountNumber?.toString() || '',
        accountName: loc.accountDescription || loc.locationName || '',
        unitNumber: loc.unitNumber || '',
        unitName: loc.unitDescription || '',
      }));
      this.fg.controls.accounts.setValue({
        includeType: this.alert.locationIncludeType || 'Include',
        accounts,
      } as any);
    }

    if ((this.alert as any).structures) {
      this.fg.controls.structures.setValue((this.alert as any).structures);
    } else if (
      locationMethod === 'Structure' &&
      this.alert.locations?.length > 0
    ) {
      const nodes = this.alert.locations.map((loc) => ({
        corpStructId: `node-${loc.corpStructNodeID}`,
        name: loc.locationName || '',
        accountNumber: loc.accountNumber || null,
        unitNumber: loc.unitNumber || null,
        code: loc.code || null,
        node: loc.corpStructNodeID,
      }));
      this.fg.controls.structures.setValue({
        includeType: this.alert.locationIncludeType || 'Include',
        nodes,
      } as any);
    }

    if ((this.alert as any).days != null) {
      this.fg.controls.days.setValue((this.alert as any).days);
    }

    if ((this.alert as any).notifyDays != null) {
      this.fg.controls.notifyDays.setValue((this.alert as any).notifyDays);
    }

    if ((this.alert as any).amount != null) {
      this.fg.controls.amount.setValue((this.alert as any).amount);
    }

    if ((this.alert as any).amountOp) {
      this.fg.controls.amountOp.setValue((this.alert as any).amountOp);
    }

    if ((this.alert as any).selectOp) {
      this.fg.controls.selectOp.setValue((this.alert as any).selectOp);
    }

    if ((this.alert as any).selectItems) {
      this.fg.controls.selectItems.setValue((this.alert as any).selectItems);
    }

    if ((this.alert as any).selectPickListItems) {
      this.fg.controls.selectPickListItems.setValue(
        (this.alert as any).selectPickListItems,
      );
    }

    if ((this.alert as any).selectBoolean != null) {
      this.fg.controls.selectBoolean.setValue(
        (this.alert as any).selectBoolean,
      );
    }

    if ((this.alert as any).financial) {
      this.fg.controls.financial.setValue((this.alert as any).financial);
    }

    if ((this.alert as any).percent != null) {
      this.fg.controls.percent.setValue((this.alert as any).percent);
    }

    if ((this.alert as any).individuals) {
      this.fg.controls.individuals.setValue((this.alert as any).individuals);
    }

    if ((this.alert as any).customField) {
      this.fg.controls.customField.setValue((this.alert as any).customField);
    }

    if ((this.alert as any).lagFrom) {
      this.fg.controls.lagFrom.setValue((this.alert as any).lagFrom);
    }

    if ((this.alert as any).lagTo) {
      this.fg.controls.lagTo.setValue((this.alert as any).lagTo);
    }

    if ((this.alert as any).lagOp) {
      this.fg.controls.lagOp.setValue((this.alert as any).lagOp);
    }

    if ((this.alert as any).lagDays != null) {
      this.fg.controls.lagDays.setValue((this.alert as any).lagDays);
    }
  }

  previous() {
    this.saveFormToAlert();
    this.stepper.previous();
  }

  next() {
    this.saveFormToAlert();
    this.stepper.next();
  }

  private saveFormToAlert() {
    this.alert.linesOfBusiness = this.fg.controls.lob.value;

    // Map state selections directly to WCF model format (State[])
    this.alert.statesOfJurisdiction = this.mapFormStatesToModel(
      this.fg.controls.jurisdictionState.value,
    );
    this.alert.statesOfLoss = this.mapFormStatesToModel(
      this.fg.controls.lossState.value,
    );
    this.alert.statesOfPayroll = this.mapFormStatesToModel(
      this.fg.controls.payrollState.value,
    );
    this.alert.statesOfPremium = this.mapFormStatesToModel(
      this.fg.controls.premiumState.value,
    );

    if (this.suppressClientLocations) {
      (this.alert as any).locationMethod = null;
      (this.alert as any).accounts = null;
      (this.alert as any).structures = null;
      // Agent users have no location selection UI. Send CAU so the Alerts Processor
      // (which does not accept LocationLookupType.Unknown) can validate the alert.
      this.alert.locationLookupType = 'CAU';
      this.alert.locationIncludeType = 'Include';
    } else {
      (this.alert as any).locationMethod =
        this.fg.controls.locationMethod.value;
      (this.alert as any).accounts = this.fg.controls.accounts.value;
      (this.alert as any).structures = this.fg.controls.structures.value;
    }

    this.alert.locations = [];
    (this.alert as any).days = this.fg.controls.days.value;
    (this.alert as any).notifyDays = this.fg.controls.notifyDays.value;
    (this.alert as any).amount = this.fg.controls.amount.value;
    (this.alert as any).amountOp = this.fg.controls.amountOp.value;
    (this.alert as any).selectOp = this.fg.controls.selectOp.value;
    (this.alert as any).selectItems = this.fg.controls.selectItems.value;
    (this.alert as any).selectPickListItems =
      this.fg.controls.selectPickListItems.value;
    (this.alert as any).selectBoolean = this.fg.controls.selectBoolean.value;
    (this.alert as any).financial = this.fg.controls.financial.value;
    (this.alert as any).percent = this.fg.controls.percent.value;
    (this.alert as any).individuals = this.fg.controls.individuals.value;
    (this.alert as any).customField = this.fg.controls.customField.value;
    (this.alert as any).lagFrom = this.fg.controls.lagFrom.value;
    (this.alert as any).lagTo = this.fg.controls.lagTo.value;
    (this.alert as any).lagOp = this.fg.controls.lagOp.value;
    (this.alert as any).lagDays = this.fg.controls.lagDays.value;

    this.alerts.currentAlert.set(this.alert);
  }

  private setStateFromModel(control: FormControl, states: State[]): void {
    if (states?.length > 0) {
      control.setValue(states.map((s) => s.code) as any);
    }
  }

  private mapFormStatesToModel(formValues: any): State[] {
    if (!Array.isArray(formValues) || formValues.length === 0) {
      return [];
    }

    return formValues.map((s: any) => {
      const code = typeof s === 'string' ? s : s.value || s.code;
      const match = this.states?.find((st) => st.value === code);

      // List item descriptions are in "CODE - NAME" format (e.g., "AL - ALABAMA").
      // WCF State.Name should contain just the name portion (e.g., "ALABAMA") to match classic XML.
      let name = code;
      if (match?.description) {
        const dashIdx = match.description.indexOf(' - ');
        name =
          dashIdx >= 0
            ? match.description.substring(dashIdx + 3)
            : match.description;
      }

      return { code, name };
    });
  }
}
